import { useState, useRef, useCallback } from "react";

interface ImageComparisonSliderProps {
  leftImage: string;
  rightImage: string;
  leftAlt: string;
  rightAlt: string;
}

export const ImageComparisonSlider = ({
  leftImage,
  rightImage,
  leftAlt,
  rightAlt
}: ImageComparisonSliderProps) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback((clientY: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const y = clientY - rect.top;
    const percentage = Math.max(0, Math.min(100, y / rect.height * 100));
    setSliderPosition(percentage);
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    setIsDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updatePosition(e.clientY);
  }, [updatePosition]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    updatePosition(e.clientY);
  }, [isDragging, updatePosition]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[3/4] overflow-hidden rounded-sm cursor-row-resize select-none touch-none bg-card"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      role="slider"
      aria-label="Image comparison slider"
      aria-valuenow={Math.round(sliderPosition)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowUp") setSliderPosition((p) => Math.max(0, p - 2));
        if (e.key === "ArrowDown") setSliderPosition((p) => Math.min(100, p + 2));
      }}>

      {/* Bottom image (full background) */}
      <div className="absolute inset-0 bg-background flex items-center justify-center p-6">
        <img
          src={rightImage}
          alt={rightAlt}
          loading="lazy"
          className="w-full h-full object-contain" />

      </div>

      {/* Top image (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ height: `${sliderPosition}%` }}>

        <div
          className="absolute inset-0 bg-background flex items-center justify-center p-6"
          style={{
            height: containerRef.current ?
            `${containerRef.current.offsetHeight}px` :
            "100%"
          }}>

          <img
            src={leftImage}
            alt={leftAlt}
            loading="lazy"
            className="w-full h-full object-contain" />

        </div>
      </div>

      {/* Slider line */}
      <div
        className="absolute left-0 right-0 h-[2px] bg-foreground/80 z-10"
        style={{ top: `${sliderPosition}%`, transform: "translateY(-50%)" }}>

        {/* Handle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full border-2 border-foreground/80 bg-background/50 backdrop-blur-sm flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-foreground/80">
            <path d="M10 6L10 2M10 2L7.5 4.5M10 2L12.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 14L10 18M10 18L7.5 15.5M10 18L12.5 15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    </div>);

};